import { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from "recharts";

// ---------------------------------------------------------------------------
// Airtable config
// ---------------------------------------------------------------------------
const TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE  = process.env.REACT_APP_AIRTABLE_BASE_ID;
const TABLE = "R2 Deliveries";
const API   = `https://api.airtable.com/v0/${BASE}/${encodeURIComponent(TABLE)}`;
const HDRS  = { "Authorization": `Bearer ${TOKEN}`, "Content-Type": "application/json" };

const fetchAll = async () => {
  let records = [], offset = null;
  do {
    const url = API + "?pageSize=100&filterByFormula=NOT({Deleted})" + (offset ? `&offset=${offset}` : "");
    const res  = await fetch(url, { headers: HDRS });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    records = [...records, ...(data.records || [])];
    offset  = data.offset || null;
  } while (offset);
  return records;
};

const fetchAllAdmin = async () => {
  let records = [], offset = null;
  do {
    const url = API + "?pageSize=100" + (offset ? `&offset=${offset}` : "");
    const res  = await fetch(url, { headers: HDRS });
    const data = await res.json();
    if (data.error) throw new Error(data.error.message);
    records = [...records, ...(data.records || [])];
    offset  = data.offset || null;
  } while (offset);
  return records;
};

const fromAirtable = r => ({
  id:               r.id,
  submittedAt:      r.fields.Submitted         || "",
  isLegacy:         r.fields.IsLegacy          || "no",
  r1Date:           r.fields.R1Date            || "",
  reservationDate:  r.fields.ReservationDate   || "",
  orderType:        r.fields.OrderType         || "",
  state:            r.fields.State             || "",
  hub:              r.fields.Hub               || "",
  color:            r.fields.Color             || "",
  interior:         r.fields.Interior          || "",
  wheels:           r.fields.Wheels            || "",
  launchPackage:    r.fields.LaunchPackage     || "no",
  firstName:        r.fields.FirstName         || "",
  lastInitial:      r.fields.LastInitial       || "",
  firstContactDate: r.fields.FirstContactDate  || "",
  contactType:      r.fields.ContactType       || "",
  deliveryDate:     r.fields.DeliveryDate      || "",
  referral:         r.fields.Referral          || "",
  notes:            r.fields.Notes             || "",
  flags:            r.fields.Flags             || 0,
  deleted:          r.fields.Deleted           || false,
});

const toAirtable = e => ({
  Submitted:        e.submittedAt,
  IsLegacy:         e.isLegacy,
  R1Date:           e.r1Date,
  ReservationDate:  e.reservationDate,
  OrderType:        e.orderType,
  State:            e.state,
  Hub:              e.hub,
  Color:            e.color,
  Interior:         e.interior,
  Wheels:           e.wheels,
  LaunchPackage:    e.launchPackage,
  FirstName:        e.firstName,
  LastInitial:      e.lastInitial,
  FirstContactDate: e.firstContactDate,
  ContactType:      e.contactType,
  DeliveryDate:     e.deliveryDate,
  Referral:         e.referral,
  Notes:            e.notes,
  Flags:            e.flags,
  Deleted:          e.deleted,
});

const airtablePost  = async fields => fetch(API, { method:"POST",  headers:HDRS, body:JSON.stringify({ fields }) }).then(r=>r.json());
const airtablePatch = async (id, fields) => fetch(`${API}/${id}`, { method:"PATCH", headers:HDRS, body:JSON.stringify({ fields }) }).then(r=>r.json());

// ---------------------------------------------------------------------------
// Admin password — change before going live
// ---------------------------------------------------------------------------
const APASS = "r2nexus2026";

// ---------------------------------------------------------------------------
// Constants — R2 Performance specs
// ---------------------------------------------------------------------------
const STATES   = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming","Washington DC"];
const ORDERS   = ["Legacy R1 Conversion","Fresh R2 Reservation"];
const CONTACTS = [{v:"generic",l:"Generic prep email"},{v:"window",l:"Specific delivery window"},{v:"call",l:"Direct call from Rivian"},{v:"other",l:"Other"}];
const REFERRALS= ["Reddit (r/Rivian)","Reddit (r/electricvehicles)","Rivian Forums","nexusEVnews","Twitter / X","Facebook Group","Friend / word of mouth","Other"];

// Performance colors (Launch Green only with Launch Package)
const COLORS_STANDARD   = ["Esker Silver","Borealis","Catalina Cove","Half Moon Grey","Forest Green","Glacier White","Midnight"];
const COLORS_LAUNCH_PKG = ["Esker Silver","Borealis","Catalina Cove","Half Moon Grey","Forest Green","Glacier White","Midnight","Launch Green (Launch Package exclusive)"];

const INTERIORS = ["Black Crater Signature (available now)","Coastal Cloud Signature (available later 2026)"];
const WHEELS    = ["21-inch All-Season (standard)","20-inch All-Terrain (optional upgrade)"];

const BLANK = {
  isLegacy:"no", r1Date:"", reservationDate:"", orderType:"",
  state:"", hub:"", color:"", interior:"", wheels:"",
  launchPackage:"no", firstName:"", lastInitial:"",
  firstContactDate:"", contactType:"", deliveryDate:"",
  referral:"", notes:""
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const daysGap = (a,b) => (!a||!b) ? null : Math.round((new Date(b)-new Date(a))/86400000);
const fmt     = d => !d ? "--" : new Date(d+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
const wkOf    = d => {
  if (!d) return null;
  const dt = new Date(d+"T12:00:00");
  const off = dt.getDay()===0 ? 6 : dt.getDay()-1;
  const m = new Date(dt); m.setDate(dt.getDate()-off);
  return m.toISOString().slice(0,10);
};

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const AC="#00d4ff", BG="#ffffff", SU="#f2f5f7", S2="#e4e8ed", BR="#cdd3db";
const TX="#1a1d24", MU="#6b7280", GR="#16a34a", AM="#d97706", RE="#dc2626", PU="#7c3aed";

const S = {
  app:   { background:"#ffffff", minHeight:"100vh", color:TX, fontFamily:"'DM Sans',sans-serif", overflowX:"hidden", width:"100%" },
  hdr:   { background:"rgba(10,11,15,0.97)", borderBottom:`1px solid ${BR}`, padding:"0 0.75rem", display:"flex", alignItems:"center", justifyContent:"space-between", height:62, position:"sticky", top:0, zIndex:50, overflowX:"hidden", width:"100%", boxSizing:"border-box" },
  logo:  { fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.55rem", letterSpacing:"0.06em", color:TX, textDecoration:"none", display:"flex", alignItems:"center", gap:"0.5rem" },
  nav:   { display:"flex", gap:"0.1rem" },
  nb:    a => ({ background:a?"rgba(255,255,255,0.12)":"transparent", border:"none", color:a?"#e8eaf0":"rgba(255,255,255,0.55)", fontSize:"0.68rem", fontWeight:500, letterSpacing:"0.06em", textTransform:"uppercase", padding:"0.32rem 0.65rem", borderRadius:4, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }),
  main:  { maxWidth:1060, margin:"0 auto", padding:"2rem 1rem 5rem", boxSizing:"border-box", width:"100%" },
  chip:  { fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", letterSpacing:"0.12em", textTransform:"uppercase", color:MU, marginBottom:"0.45rem", display:"block" },
  h1:    { fontFamily:"'Bebas Neue',sans-serif", fontSize:"clamp(2rem,4vw,2.8rem)", lineHeight:1.05, letterSpacing:"0.02em", marginBottom:"0.45rem" },
  card:  { background:SU, border:`1px solid ${BR}`, borderRadius:6, padding:"1.2rem" },
  sc:    { background:SU, border:`1px solid ${BR}`, borderRadius:6, padding:"1.1rem 1.25rem", textAlign:"center" },
  sn:    { fontFamily:"'Bebas Neue',sans-serif", fontSize:"2.1rem", lineHeight:1, color:AC },
  sl:    { fontFamily:"'DM Mono',monospace", fontSize:"0.57rem", letterSpacing:"0.1em", textTransform:"uppercase", color:MU, marginTop:"0.28rem" },
  g4:    { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(135px,1fr))", gap:10, marginBottom:"1.6rem" },
  g2:    { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))", gap:12, marginBottom:"1.4rem" },
  badge: (c,bg) => ({ display:"inline-block", fontSize:"0.57rem", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", textTransform:"uppercase", color:c, background:bg, padding:"2px 7px", borderRadius:3 }),
  lbl:   { display:"block", fontSize:"0.73rem", color:MU, marginBottom:"0.28rem", fontFamily:"'DM Mono',monospace", letterSpacing:"0.04em" },
  inp:   { width:"100%", background:"#ffffff", border:`1px solid ${BR}`, borderRadius:4, color:TX, padding:"0.48rem 0.68rem", fontSize:"0.87rem", fontFamily:"'DM Sans',sans-serif", outline:"none", boxSizing:"border-box" },
  sel:   { width:"100%", background:"#ffffff", border:`1px solid ${BR}`, borderRadius:4, color:TX, padding:"0.48rem 0.68rem", fontSize:"0.87rem", fontFamily:"'DM Sans',sans-serif", outline:"none", boxSizing:"border-box", appearance:"none" },
  btn:   { background:AC, color:"#000", border:"none", borderRadius:4, padding:"0.55rem 1.25rem", fontSize:"0.72rem", fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" },
  ghost: { background:"transparent", color:MU, border:`1px solid ${BR}`, borderRadius:4, padding:"0.52rem 1.05rem", fontSize:"0.7rem", fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", transition:"border-color 0.15s" },
  sm:    { background:"transparent", color:MU, border:`1px solid ${BR}40`, borderRadius:3, padding:"0.2rem 0.52rem", fontSize:"0.62rem", fontFamily:"'DM Mono',monospace", cursor:"pointer" },
  del:   { background:"transparent", color:RE, border:`1px solid ${RE}35`, borderRadius:3, padding:"0.2rem 0.52rem", fontSize:"0.62rem", fontFamily:"'DM Mono',monospace", cursor:"pointer" },
  rst:   { background:"transparent", color:GR, border:`1px solid ${GR}35`, borderRadius:3, padding:"0.2rem 0.52rem", fontSize:"0.62rem", fontFamily:"'DM Mono',monospace", cursor:"pointer" },
  fr:    { marginBottom:"0.9rem" },
  fg:    { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))", gap:14 },
  th:    { fontFamily:"'DM Mono',monospace", fontSize:"0.57rem", letterSpacing:"0.1em", textTransform:"uppercase", color:MU, padding:"0.65rem 0.75rem", textAlign:"left", borderBottom:`1px solid ${BR}`, background:S2, whiteSpace:"nowrap" },
  td:    { fontSize:"0.79rem", padding:"0.58rem 0.75rem", borderBottom:`1px solid ${BR}`, verticalAlign:"middle", color:TX },
  disc:  { background:`${AM}12`, border:`1px solid ${AM}30`, borderRadius:6, padding:"0.95rem 1.1rem", fontSize:"0.79rem", color:MU, lineHeight:1.65, marginBottom:"1.3rem" },
  lnk:   { color:AC, textDecoration:"none" },
  ftr:   { borderTop:`1px solid ${BR}`, padding:"2rem 1rem", textAlign:"center", fontFamily:"'DM Mono',monospace", fontSize:"0.59rem", color:MU, lineHeight:2, overflowX:"hidden", background:"#f2f5f7" },
  tw:    { overflowX:"auto", borderRadius:6, border:`1px solid ${BR}` },
  err:   { color:RE, fontSize:"0.79rem", marginBottom:"0.9rem", padding:"0.68rem 0.95rem", background:`${RE}10`, borderRadius:4 },
};


// ---------------------------------------------------------------------------
// Paint color map for charts (matches actual R2 paint colors)
// ---------------------------------------------------------------------------
const PAINT_COLORS = {
  "Esker Silver":  "#8e9299",
  "Glacier White": "#dde0db",
  "Midnight":      "#1c1e21",
  "Catalina Cove": "#1d3a4e",
  "Half Moon Grey":"#52575e",
  "Forest Green":  "#2e4428",
  "Launch Green":  "#4a7a2a",
  "Borealis":      "#2a456e",
};

const INTERIOR_COLORS = {
  "Black Crater Signature":  "#2a2c2d",
  "Coastal Cloud Signature": "#bfbab3",
};

const STATE_ABBR = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA",
  "Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA",
  "Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA",
  "Kansas":"KS","Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD",
  "Massachusetts":"MA","Michigan":"MI","Minnesota":"MN","Mississippi":"MS",
  "Missouri":"MO","Montana":"MT","Nebraska":"NE","Nevada":"NV",
  "New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY",
  "North Carolina":"NC","North Dakota":"ND","Ohio":"OH","Oklahoma":"OK",
  "Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC",
  "South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT",
  "Vermont":"VT","Virginia":"VA","Washington":"WA","West Virginia":"WV",
  "Wisconsin":"WI","Wyoming":"WY","Washington DC":"DC"
};

const StateTick = ({ x, y, payload }) => {
  const abbr = STATE_ABBR[payload.value] || payload.value.slice(0,2).toUpperCase();
  return (
    <g transform={`translate(${x},${y})`}>
      {/* State name — anchored to end, sitting left of the badge */}
      <text x={-36} y={0} dy="0.35em" textAnchor="end"
        fill={MU} fontSize={11} fontFamily="'DM Sans',sans-serif">
        {payload.value}
      </text>
      {/* Abbreviation badge — close to the axis line */}
      <rect x={-32} y={-9} width={28} height={17} rx={3}
        fill={"#00d4ff22"} stroke={"#00d4ff60"} strokeWidth={1}/>
      <text x={-18} y={0} dy="0.35em" textAnchor="middle"
        fill={"#00d4ff"} fontSize={8} fontWeight={700} fontFamily="'DM Mono',monospace">
        {abbr}
      </text>
    </g>
  );
};

// ---------------------------------------------------------------------------
// Shared sub-components (defined OUTSIDE App to prevent remount on render)
// ---------------------------------------------------------------------------
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"#ffffff", border:"1px solid #cdd3db", boxShadow:"0 2px 8px rgba(0,0,0,0.12)", padding:"8px 12px", borderRadius:4, fontFamily:"'DM Mono',monospace", fontSize:11 }}>
      <div style={{ color:MU, marginBottom:2 }}>{label}</div>
      <div style={{ color:AC, fontWeight:500 }}>{payload[0].value}</div>
    </div>
  );
};

const Logo = () => (
  <a href="https://nexusevnews.com" target="_blank" rel="noopener noreferrer" style={S.logo}>
    <svg width="26" height="29" viewBox="0 0 36 40" fill="none" aria-hidden="true">
      <polygon points="18,2 34,11 34,29 18,38 2,29 2,11" fill="rgba(0,212,255,0.07)" stroke="rgba(0,212,255,0.35)" strokeWidth="1"/>
      <polygon points="18,4 32,12 32,28 18,36 4,28 4,12" fill="rgba(0,212,255,0.1)" stroke="#00d4ff" strokeWidth="1.5"/>
      <path d="M22 9L12 22H20L14 32L26 18H18L22 9Z" fill="#00d4ff"/>
    </svg>
    <span>NEXUS<span style={{color:AC}}>EV</span><span style={{fontSize:"0.52em",color:TX,letterSpacing:"0.02em"}}>news</span></span>
  </a>
);

// ---------------------------------------------------------------------------
// Overview component (outside App)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Estimate Modal
// ---------------------------------------------------------------------------
const EBLANK = { isLegacy:"no", r1Date:"", reservationDate:"", color:"", interior:"", wheels:"" };

const EstimateModal = ({ entries, onClose }) => {
  const [form, setForm] = useState({...EBLANK});
  const [result, setResult] = useState(null);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const colorOptions = COLORS_LAUNCH_PKG;

  const run = () => {
    if (!form.reservationDate) return;

    const resDate = new Date(form.reservationDate + "T12:00:00");

    // Find submissions that match profile as closely as possible
    // Score each entry: +2 for same legacy status, +1 for same color, +1 for same interior, +1 for same wheels
    const scored = entries.map(e => {
      let score = 0;
      if (e.isLegacy === form.isLegacy) score += 2;
      if (form.color    && e.color    === form.color)    score += 1;
      if (form.interior && e.interior === form.interior) score += 1;
      if (form.wheels   && e.wheels   === form.wheels)   score += 1;
      return { ...e, score };
    }).filter(e => e.score > 0);

    // All entries with first contact date
    const withContact = scored.filter(e => e.firstContactDate && e.reservationDate);
    // All entries with delivery date
    const withDelivery = scored.filter(e => e.deliveryDate && e.firstContactDate);

    // Avg weeks from reservation to first contact
    let avgContactWeeks = null;
    if (withContact.length > 0) {
      const gaps = withContact.map(e => {
        const res = new Date(e.reservationDate + "T12:00:00");
        const con = new Date(e.firstContactDate + "T12:00:00");
        return (con - res) / (1000 * 60 * 60 * 24 * 7);
      });
      avgContactWeeks = gaps.reduce((a,b)=>a+b,0) / gaps.length;
    }

    // Avg weeks from first contact to delivery
    let avgDeliveryWeeks = null;
    if (withDelivery.length > 0) {
      const gaps = withDelivery.map(e => {
        const con = new Date(e.firstContactDate + "T12:00:00");
        const del = new Date(e.deliveryDate + "T12:00:00");
        return (del - con) / (1000 * 60 * 60 * 24 * 7);
      });
      avgDeliveryWeeks = gaps.reduce((a,b)=>a+b,0) / gaps.length;
    }

    // Project dates
    let estContact = null, estDelivery = null;
    if (avgContactWeeks !== null) {
      estContact = new Date(resDate.getTime() + avgContactWeeks * 7 * 24 * 60 * 60 * 1000);
    }
    if (estContact && avgDeliveryWeeks !== null) {
      estDelivery = new Date(estContact.getTime() + avgDeliveryWeeks * 7 * 24 * 60 * 60 * 1000);
    }

    setResult({
      estContact,
      estDelivery,
      contactSampleSize: withContact.length,
      deliverySampleSize: withDelivery.length,
      totalMatches: scored.length,
    });
  };

  const fmtEst = d => !d ? null : d.toLocaleDateString("en-US", { month:"long", year:"numeric" });

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:200,
      background:"rgba(0,0,0,0.82)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"1rem",
    }} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{
        background:"#111318", border:"1px solid #1f2330",
        borderRadius:8, padding:"2rem",
        maxWidth:560, width:"100%",
        maxHeight:"90vh", overflowY:"auto",
        position:"relative",
      }}>
        <button onClick={onClose} style={{
          position:"absolute", top:"1rem", right:"1rem",
          background:"transparent", border:"none",
          color:MU, fontSize:"1.4rem", cursor:"pointer", lineHeight:1,
        }}>×</button>

        <span style={S.chip}>Delivery Estimator</span>
        <h2 style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.8rem", letterSpacing:"0.03em", marginBottom:"0.4rem", color:TX}}>
          Estimate Your <span style={{color:AC}}>Delivery Date</span>
        </h2>
        <p style={{color:MU, fontSize:"0.82rem", lineHeight:1.7, marginBottom:"1.5rem"}}>
          Tell us about your order and we'll compare it against real submissions in the tracker to give you a rough idea of timing. The more data we have, the better this gets.
        </p>

        {!result ? (
          <>
            <div style={{background:"#1a1d24", border:"1px solid #1f2330", borderRadius:6, padding:"1.2rem", marginBottom:10}}>
              <span style={{...S.chip, color:"rgba(255,255,255,0.4)"}}>Your order</span>
              <div style={S.fg}>
                <div style={S.fr}>
                  <label style={S.lbl}>Do you own an R1T or R1S?</label>
                  <div style={{display:"flex", gap:"1.2rem", marginTop:"0.15rem"}}>
                    {[["yes","Yes, R1 owner"],["no","No"]].map(([v,l])=>(
                      <label key={v} style={{display:"flex",alignItems:"center",gap:"0.38rem",fontSize:"0.85rem",cursor:"pointer",color:form.isLegacy===v?TX:MU}}>
                        <input type="radio" name="e_leg" value={v} checked={form.isLegacy===v} onChange={()=>sf("isLegacy",v)} style={{accentColor:AC}}/>{l}
                      </label>
                    ))}
                  </div>
                </div>
                {form.isLegacy==="yes" && (
                  <div style={S.fr}>
                    <label style={S.lbl}>R1 original delivery date</label>
                    <input style={S.inp} type="date" value={form.r1Date} onChange={e=>sf("r1Date",e.target.value)}/>
                  </div>
                )}
                <div style={S.fr}>
                  <label style={S.lbl}>R2 reservation date *</label>
                  <input style={S.inp} type="date" value={form.reservationDate} onChange={e=>sf("reservationDate",e.target.value)}/>
                </div>
              </div>
            </div>

            <div style={{background:"#1a1d24", border:"1px solid #1f2330", borderRadius:6, padding:"1.2rem", marginBottom:16}}>
              <span style={{...S.chip, color:"rgba(255,255,255,0.4)"}}>Your configuration</span>
              <div style={S.fg}>
                <div style={S.fr}>
                  <label style={S.lbl}>Exterior color</label>
                  <select style={S.sel} value={form.color} onChange={e=>sf("color",e.target.value)}>
                    <option value="">Select</option>
                    {colorOptions.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={S.fr}>
                  <label style={S.lbl}>Interior</label>
                  <select style={S.sel} value={form.interior} onChange={e=>sf("interior",e.target.value)}>
                    <option value="">Select</option>
                    {INTERIORS.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={S.fr}>
                  <label style={S.lbl}>Wheels</label>
                  <select style={S.sel} value={form.wheels} onChange={e=>sf("wheels",e.target.value)}>
                    <option value="">Select</option>
                    {WHEELS.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <button style={S.btn} onClick={run} disabled={!form.reservationDate}>
              Run Estimate
            </button>
          </>
        ) : (
          <>
            <div style={{
              background:`linear-gradient(135deg, #0d1a12 0%, #0a0f1a 100%)`,
              border:`1px solid ${AC}35`,
              borderRadius:6,
              padding:"1.5rem",
              marginBottom:12,
            }}>
              {result.totalMatches === 0 ? (
                <div style={{textAlign:"center", padding:"1.25rem 0.5rem"}}>
                  <div style={{fontSize:"1.5rem", marginBottom:"0.6rem"}}>🙏</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.3rem", letterSpacing:"0.04em", color:TX, marginBottom:"0.6rem"}}>
                    Thanks for your input!
                  </div>
                  <div style={{color:MU, fontSize:"0.84rem", lineHeight:1.75, marginBottom:"1.2rem"}}>
                    We need just a few more submissions to accurately calculate an estimated delivery for your configuration. If you'd like to help us get there, add your own data below — every entry counts.
                  </div>
                  <button style={S.btn} onClick={onClose}>
                    Submit Your R2 Data
                  </button>
                </div>
              ) : (
                <>
                  <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:MU, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:"1rem"}}>
                    Based on {result.totalMatches} similar {result.totalMatches===1?"profile":"profiles"} in the tracker
                  </div>

                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:"1rem"}}>
                    <div style={{textAlign:"center", padding:"1rem", background:`${S2}`, borderRadius:5}}>
                      <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:MU, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"0.4rem"}}>
                        Est. Rivian contact
                      </div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.4rem", color:fmtEst(result.estContact)?AC:`${AC}90`, lineHeight:1.2, letterSpacing:"0.03em"}}>
                        {fmtEst(result.estContact) || "More Submissions Needed"}
                      </div>
                      {result.contactSampleSize > 0 && (
                        <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:MU, marginTop:"0.3rem"}}>
                          {result.contactSampleSize} data {result.contactSampleSize===1?"point":"points"}
                        </div>
                      )}
                    </div>
                    <div style={{textAlign:"center", padding:"1rem", background:`${S2}`, borderRadius:5}}>
                      <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:MU, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"0.4rem"}}>
                        Est. delivery
                      </div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.4rem", color:fmtEst(result.estDelivery)?GR:`${GR}90`, lineHeight:1.2, letterSpacing:"0.03em"}}>
                        {fmtEst(result.estDelivery) || "More Submissions Needed"}
                      </div>
                      {result.deliverySampleSize > 0 && (
                        <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.58rem", color:MU, marginTop:"0.3rem"}}>
                          {result.deliverySampleSize} data {result.deliverySampleSize===1?"point":"points"}
                        </div>
                      )}
                    </div>
                  </div>
                  {(!fmtEst(result.estContact) || !fmtEst(result.estDelivery)) && (
                    <div style={{
                      marginTop:"1rem",
                      padding:"0.85rem 1rem",
                      background:`${AC}08`,
                      border:`1px solid ${AC}25`,
                      borderRadius:5,
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"space-between",
                      gap:12,
                      flexWrap:"wrap",
                    }}>
                      <span style={{fontSize:"0.8rem", color:MU, fontFamily:"'DM Sans',sans-serif", lineHeight:1.5}}>
                        Not enough delivery data yet for this configuration.
                      </span>
                      <button onClick={onClose} style={{
                        background:AC, color:"#000", border:"none", borderRadius:4,
                        padding:"0.38rem 0.9rem", fontSize:"0.72rem", fontWeight:700,
                        letterSpacing:"0.04em", textTransform:"uppercase",
                        cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                        whiteSpace:"nowrap", flexShrink:0,
                      }}>Add Your Data</button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{
              background:`${AM}10`,
              border:`1px solid ${AM}25`,
              borderRadius:6,
              padding:"1rem",
              fontSize:"0.75rem",
              color:MU,
              lineHeight:1.75,
              marginBottom:14,
            }}>
              <strong style={{color:AM}}>Important:</strong> This estimate is generated entirely from self-reported community data and is provided for informational purposes only. It is not a guarantee, prediction, or commitment of any kind regarding your Rivian R2 delivery timeline. Rivian Automotive, Inc. has not reviewed, approved, or endorsed this tool or any estimate it produces. Actual contact and delivery timing will vary based on your specific order, Rivian's production schedule, regional logistics, and factors outside anyone's control. nexusEVnews makes no warranties, express or implied, about the accuracy of this estimate. Do not make financial, travel, or other binding decisions based on this information.
            </div>

            <div style={{display:"flex", gap:10}}>
              <button style={S.ghost} onClick={()=>setResult(null)}>Try Different Inputs</button>
              <button style={S.btn} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Overview = ({ entries, loading, error, stats, charts, setTab, emailVal, setEmailVal, emailDone, setEmailDone, copied, setCopied, openEstimate }) => {
  const shareSnap = () => [
    "Rivian R2 Delivery Tracker -- community snapshot",
    "",
    `${stats.total} total submissions from ${stats.states} states`,
    `${stats.delivered} confirmed deliveries`,
    stats.avgDays ? `Avg ${stats.avgDays} days from first Rivian contact to keys in hand` : "",
    "",
    "Add your own data: https://rivian-r2-tracker.nexusevnews.com",
    "",
    "All data is self-reported by community members.",
  ].filter(Boolean).join("\n");

  const copySnap = () => {
    navigator.clipboard.writeText(shareSnap()).then(() => { setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };

  // Sort delivered entries by delivery date ascending — earliest first
  const firstDeliveries = useMemo(() => {
    return entries
      .filter(e => e.deliveryDate)
      .sort((a, b) => a.deliveryDate.localeCompare(b.deliveryDate))
      .slice(0, 10);
  }, [entries]);

  const medalColor = i => i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : MU;

  return (
    <div>
      {error && <div style={S.err}>{error}</div>}

      {/* ------------------------------------------------------------------ */}
      {/* FIRST DELIVERIES PANEL — top of page                               */}
      {/* ------------------------------------------------------------------ */}
      {!loading && (
        <div style={{marginBottom:14}}>
          {firstDeliveries.length === 0 ? (
            <div style={{
              background: "linear-gradient(135deg, #111318 0%, #0d1520 100%)",
              border: `1px solid ${AC}30`,
              borderRadius: 8,
              padding: "1rem 1.5rem",
              textAlign: "center",
              marginBottom: 14,
              position: "relative",
              overflow: "hidden"
            }}>
              <div style={{
                fontFamily:"'Bebas Neue',sans-serif",
                fontSize:"clamp(1.4rem,3vw,2rem)",
                letterSpacing:"0.04em",
                color:"#e8eaf0",
                marginBottom:"0"
              }}>
                First Delivery: <span style={{color:AC}}>Not Yet Claimed</span>
              </div>
            </div>
          ) : (
            <div style={{
              background: "linear-gradient(135deg, #111318 0%, #0d1520 100%)",
              border: `1px solid ${AC}40`,
              borderRadius: 8,
              padding: "1.5rem",
              marginBottom: 14,
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Glow */}
              <div style={{
                position:"absolute", top:-60, right:-60,
                width:220, height:220,
                background:`radial-gradient(circle, ${AC}12 0%, transparent 70%)`,
                pointerEvents:"none"
              }}/>

              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:"1.25rem"}}>
                <div>
                  <span style={{...S.chip, color:"rgba(255,255,255,0.45)"}}>First Deliveries</span>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.5rem", letterSpacing:"0.04em", color:TX, lineHeight:1}}>
                    Community Members With Keys
                  </div>
                </div>
                <span style={{
                  fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", letterSpacing:"0.08em",
                  textTransform:"uppercase", color:AC,
                  background:`${AC}15`, border:`1px solid ${AC}30`,
                  padding:"4px 10px", borderRadius:3
                }}>
                  {firstDeliveries.length} delivered
                </span>
              </div>

              {/* #1 delivery — hero card */}
              {firstDeliveries[0] && (() => {
                const e = firstDeliveries[0];
                const name = [e.firstName, e.lastInitial ? e.lastInitial+"." : ""].filter(Boolean).join(" ") || "Community Member";
                return (
                  <div style={{
                    background:`linear-gradient(135deg, #1a1500 0%, #0f1a0f 100%)`,
                    border:`1px solid ${medalColor(0)}60`,
                    borderRadius:6,
                    padding:"1.1rem 1.25rem",
                    marginBottom:10,
                    display:"flex",
                    alignItems:"center",
                    gap:"1rem",
                    flexWrap:"wrap"
                  }}>
                    <div style={{
                      fontFamily:"'Bebas Neue',sans-serif",
                      fontSize:"2.5rem",
                      lineHeight:1,
                      color:medalColor(0),
                      minWidth:40,
                      textAlign:"center"
                    }}>1</div>
                    <div style={{flex:1, minWidth:160}}>
                      <div style={{
                        fontFamily:"'Bebas Neue',sans-serif",
                        fontSize:"1.4rem",
                        letterSpacing:"0.03em",
                        color:TX,
                        lineHeight:1,
                        marginBottom:"0.3rem"
                      }}>
                        {name}
                        <span style={{
                          fontFamily:"'DM Mono',monospace",
                          fontSize:"0.6rem",
                          letterSpacing:"0.1em",
                          color:medalColor(0),
                          marginLeft:"0.6rem",
                          verticalAlign:"middle",
                          background:`${medalColor(0)}18`,
                          padding:"2px 7px",
                          borderRadius:3,
                          textTransform:"uppercase"
                        }}>First</span>
                      </div>
                      <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.68rem", color:MU, lineHeight:1.8}}>
                        {[e.state, e.hub].filter(Boolean).join(", ")||"--"} &nbsp;·&nbsp;
                        {e.color||"--"} &nbsp;·&nbsp;
                        {e.wheels ? e.wheels.split(" (")[0] : "--"} &nbsp;·&nbsp;
                        {e.launchPackage==="yes" ? "Launch Package" : "Standard"}
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.5rem", color:AC, lineHeight:1}}>
                        {fmt(e.deliveryDate)}
                      </div>
                      <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.6rem", color:MU, textTransform:"uppercase", letterSpacing:"0.08em"}}>
                        Delivery date
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Positions 2-10 */}
              {firstDeliveries.slice(1).map((e, idx) => {
                const rank = idx + 2;
                const name = [e.firstName, e.lastInitial ? e.lastInitial+"." : ""].filter(Boolean).join(" ") || "Community Member";
                return (
                  <div key={e.id} style={{
                    display:"flex",
                    alignItems:"center",
                    gap:"0.85rem",
                    padding:"0.7rem 0.9rem",
                    borderRadius:5,
                    background: idx % 2 === 0 ? `${S2}88` : "transparent",
                    marginBottom:2,
                    flexWrap:"wrap"
                  }}>
                    <div style={{
                      fontFamily:"'Bebas Neue',sans-serif",
                      fontSize:"1.3rem",
                      color:medalColor(rank-1),
                      minWidth:28,
                      textAlign:"center",
                      lineHeight:1
                    }}>{rank}</div>
                    <div style={{flex:1, minWidth:140}}>
                      <div style={{fontSize:"0.88rem", fontWeight:500, color:"#e8eaf0", marginBottom:"0.15rem"}}>{name}</div>
                      <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.63rem", color:MU}}>
                        {[e.state, e.hub].filter(Boolean).join(", ")||"--"} &nbsp;·&nbsp; {e.color||"--"}
                      </div>
                    </div>
                    <div style={{fontFamily:"'DM Mono',monospace", fontSize:"0.7rem", color:MU}}>{fmt(e.deliveryDate)}</div>
                  </div>
                );
              })}

              <div style={{
                marginTop:"1rem",
                paddingTop:"0.9rem",
                borderTop:`1px solid ${BR}`,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                flexWrap:"wrap",
                gap:8
              }}>
                <span style={{fontFamily:"'DM Mono',monospace", fontSize:"0.62rem", color:MU}}>
                  Sorted by delivery date. Self-reported data.
                </span>
                <button style={S.ghost} onClick={()=>setTab("submit")}>Add Your Delivery</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* CENTERED TITLE + DESCRIPTION + ESTIMATE BUTTON + STATS             */}
      {/* ------------------------------------------------------------------ */}
      <div style={{textAlign:"center", marginBottom:"2rem"}}>
        <span style={S.chip}>Community Tracker</span>
        <h1 style={{...S.h1, display:"inline-block"}}>
          Rivian R2<br/><span style={{color:AC}}>Delivery Tracker</span>
        </h1>
        <p style={{color:MU, lineHeight:1.75, fontSize:"0.9rem", maxWidth:620, margin:"0.75rem auto 2rem"}}>
          A community-built record of non-employee R2 Performance deliveries. Every data point is self-reported.
          Add yours, explore the patterns, and help the community understand who's getting their R2 and when.
        </p>
        <button
          onClick={openEstimate}
          style={{
            background: AC,
            color: "#000",
            border: "none",
            borderRadius: 6,
            padding: "0.825rem 2.25rem",
            fontSize: "1.01rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: "pointer",
            fontFamily: "'DM Sans',sans-serif",
            boxShadow: `0 0 28px ${AC}55`,
            marginBottom: "2rem",
          }}
        >Estimate My Delivery Date</button>
        <div style={{marginBottom:"1.25rem"}}>
          <button
            onClick={()=>setTab("submit")}
            style={{
              background: "transparent",
              color: AC,
              border: `1px solid ${AC}60`,
              borderRadius: 6,
              padding: "0.65rem 1.75rem",
              fontSize: "0.82rem",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif",
            }}
          >Submit Your R2 Data</button>
        </div>
        <div style={{marginTop:"0.9rem"}}>
          <a
            href="https://rivian.com/configurations/builder/r2"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily:"'DM Mono',monospace",
              fontSize:"0.72rem",
              color:"#FFB100",
              textDecoration:"none",
              letterSpacing:"0.06em",
              textTransform:"uppercase",
              borderBottom:"1px solid #FFB10060",
              paddingBottom:"1px",
            }}
          >Build Your R2 on Rivian.com →</a>
        </div>
      </div>

      <div style={{...S.g4, marginBottom:"2rem"}}>
        {[
          { n: stats.total,     l: "Total submissions" },
          { n: stats.delivered, l: "Confirmed deliveries" },
          { n: stats.avgDays !== null ? `${stats.avgDays}d` : "--", l: "Avg contact to delivery" },
          { n: stats.states,    l: "States represented" },
        ].map(({n,l}) => (
          <div key={l} style={S.sc}><div style={S.sn}>{n ?? "--"}</div><div style={S.sl}>{l}</div></div>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* CHARTS                                                               */}
      {/* ------------------------------------------------------------------ */}
      {loading ? (
        <div style={{...S.card, textAlign:"center", padding:"2.5rem", color:MU}}>Loading tracker data...</div>
      ) : entries.length > 0 && (
        <>
          {charts.timeline.length > 0 && (
            <div style={{...S.card, marginBottom:12}}>
              <span style={S.chip}>Deliveries by week</span>
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={charts.timeline} margin={{top:4,right:14,bottom:0,left:-18}}>
                  <CartesianGrid stroke={BR} vertical={false}/>
                  <XAxis dataKey="label" tick={{fill:MU,fontSize:10,fontFamily:"'DM Mono',monospace"}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fill:MU,fontSize:10,fontFamily:"'DM Mono',monospace"}} axisLine={false} tickLine={false} allowDecimals={false}/>
                  <Tooltip content={<ChartTip/>}/>
                  <Bar dataKey="n" name="deliveries" fill={AC} radius={[2,2,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Color and States: full width stacked, each tall enough to breathe */}
          {charts.colors.length > 0 && (
            <div style={{...S.card, marginBottom:12}}>
              <span style={S.chip}>Color distribution</span>
              <ResponsiveContainer width="100%" height={Math.max(240, charts.colors.length * 32)}>
                <BarChart
                  data={charts.colors}
                  layout="vertical"
                  margin={{left:8, right:40, top:4, bottom:4}}
                >
                  <XAxis
                    type="number"
                    tick={{fill:MU, fontSize:10, fontFamily:"'DM Mono',monospace"}}
                    axisLine={false} tickLine={false} allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="n"
                    width={160}
                    tick={{fill:MU, fontSize:11, fontFamily:"'DM Sans',sans-serif"}}
                    axisLine={false} tickLine={false}
                  />
                  <Tooltip content={<ChartTip/>} cursor={false}/>
                  <Bar dataKey="v" name="orders" fill={AC} radius={[0,3,3,0]}>
                    {charts.colors.map((e,i)=>(
                      <Cell key={i} fill={PAINT_COLORS[e.n] || AC}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {charts.states.length > 0 && (
            <div style={{...S.card, marginBottom:12}}>
              <span style={S.chip}>Top states</span>
              <ResponsiveContainer width="100%" height={Math.max(240, charts.states.length * 32)}>
                <BarChart
                  data={charts.states}
                  layout="vertical"
                  margin={{left:8, right:40, top:4, bottom:4}}
                >
                  <XAxis
                    type="number"
                    tick={{fill:MU, fontSize:10, fontFamily:"'DM Mono',monospace"}}
                    axisLine={false} tickLine={false} allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="n"
                    width={220}
                    tick={<StateTick/>}
                    axisLine={false} tickLine={false}
                  />
                  <Tooltip content={<ChartTip/>} cursor={false}/>
                  <Bar dataKey="v" name="entries" fill={AC} radius={[0,3,3,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {charts.interiors.length > 0 && (
            <div style={{...S.card, marginBottom:12}}>
              <span style={S.chip}>Interior breakdown</span>
              <ResponsiveContainer width="100%" height={Math.max(160, charts.interiors.length * 48)}>
                <BarChart data={charts.interiors} margin={{top:4,right:14,bottom:0,left:-18}}>
                  <CartesianGrid stroke={BR} vertical={false}/>
                  <XAxis dataKey="n" tick={{fill:MU,fontSize:11,fontFamily:"'DM Sans',monospace"}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fill:MU,fontSize:10,fontFamily:"'DM Mono',monospace"}} axisLine={false} tickLine={false} allowDecimals={false}/>
                  <Tooltip content={<ChartTip/>} cursor={false}/>
                  <Bar dataKey="v" name="entries" fill={GR} radius={[2,2,0,0]}>
                    {charts.interiors.map((e,i)=>{
                      const ic = INTERIOR_COLORS[e.n];
                      const isLight = e.n && e.n.includes("Coastal");
                      return <Cell key={i} fill={ic||GR} stroke={isLight?"#666":"none"} strokeWidth={isLight?1:0}/>;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      <div style={{...S.card, background:S2}}>
        <span style={S.chip}>Weekly updates in your inbox</span>
        <p style={{fontSize:"0.84rem", color:MU, marginBottom:"0.9rem", lineHeight:1.7}}>
          Nexus Current is the nexusEVnews newsletter. Each week we include a summary of new R2 tracker data alongside EV news. No spam, one-click unsubscribe.
        </p>
        {emailDone ? (
          <div style={{color:GR, fontSize:"0.84rem"}}>Done. Check your inbox for a confirmation from Nexus Current.</div>
        ) : (
          <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
            <input style={{...S.inp, maxWidth:270}} type="email" placeholder="your@email.com"
              value={emailVal} onChange={e=>setEmailVal(e.target.value)}/>
            <button style={S.btn} onClick={()=>{
              if (emailVal.includes("@")) {
                window.open(`https://nexuscurrent.beehiiv.com/subscribe?email=${encodeURIComponent(emailVal)}`,"_blank");
                setEmailDone(true);
              }
            }}>Subscribe</button>
          </div>
        )}
      </div>
    </div>
  );
};
const Deliveries = ({ entries, loading, flagEntry, flagMsg, exportCSV }) => {
  const [fState, setFState] = useState("");
  const [fDlv,   setFDlv]   = useState("all");
  const [fLeg,   setFLeg]   = useState("all");

  const filtered = useMemo(() => {
    let list = entries;
    if (fState)       list = list.filter(e => e.state===fState);
    if (fDlv==="yes") list = list.filter(e => e.deliveryDate);
    if (fDlv==="no")  list = list.filter(e => !e.deliveryDate);
    if (fLeg==="yes") list = list.filter(e => e.isLegacy==="yes");
    if (fLeg==="no")  list = list.filter(e => e.isLegacy!=="yes");
    return [...list].sort((a,b) => (a.reservationDate||"").localeCompare(b.reservationDate||""));
  }, [entries, fState, fDlv, fLeg]);

  return (
    <div>
      <h1 style={S.h1}>All <span style={{color:AC}}>Submissions</span></h1>
      <p style={{color:MU, fontSize:"0.84rem", marginBottom:"1.3rem"}}>
        {filtered.length} of {entries.length} entries shown.
        {flagMsg ? <span style={{color:AM, marginLeft:8}}>{flagMsg}</span> : null}
      </p>

      <div style={{display:"flex", flexWrap:"wrap", gap:7, marginBottom:"1.3rem"}}>
        <select style={{...S.sel,width:"auto",minWidth:135}} value={fState} onChange={e=>setFState(e.target.value)}>
          <option value="">All States</option>
          {STATES.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
        <select style={{...S.sel,width:"auto",minWidth:125}} value={fDlv} onChange={e=>setFDlv(e.target.value)}>
          <option value="all">All status</option>
          <option value="yes">Delivered</option>
          <option value="no">Waiting</option>
        </select>
        <select style={{...S.sel,width:"auto",minWidth:135}} value={fLeg} onChange={e=>setFLeg(e.target.value)}>
          <option value="all">All types</option>
          <option value="yes">Legacy R1 only</option>
          <option value="no">Fresh reservation</option>
        </select>
        <button style={S.ghost} onClick={()=>{setFState("");setFDlv("all");setFLeg("all");}}>Clear filters</button>
        <button style={S.ghost} onClick={exportCSV}>Export CSV</button>
      </div>

      {loading ? (
        <div style={{...S.card, textAlign:"center", padding:"2rem", color:MU}}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{...S.card, textAlign:"center", padding:"2rem", color:MU}}>No entries match your current filters.</div>
      ) : (
        <div style={S.tw}>
          <table style={{width:"100%", borderCollapse:"collapse"}}>
            <thead>
              <tr>{["Name","Type","State","City","Color","Interior","Wheels","Launch Pkg","Reservation","First Contact","Delivery","Days","Flag"]
                .map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((e,i) => {
                const d = daysGap(e.firstContactDate, e.deliveryDate);
                const name = [e.firstName, e.lastInitial ? e.lastInitial+"." : ""].filter(Boolean).join(" ") || "--";
                return (
                  <tr key={e.id} style={{background: i%2===0?SU:`${S2}88`}}>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.72rem"}}>{name}</span></td>
                    <td style={S.td}>
                      {e.isLegacy==="yes"
                        ? <span style={S.badge(AC,`${AC}18`)}>Legacy R1</span>
                        : <span style={S.badge(MU,`${MU}18`)}>Fresh</span>}
                    </td>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.71rem"}}>{e.state||"--"}</span></td>
                    <td style={S.td}>{e.hub||"--"}</td>
                    <td style={S.td}>{e.color||"--"}</td>
                    <td style={S.td}>{e.interior ? e.interior.split(" (")[0] : "--"}</td>
                    <td style={S.td}>{e.wheels ? e.wheels.split(" (")[0] : "--"}</td>
                    <td style={S.td}>{e.launchPackage==="yes" ? <span style={S.badge(GR,`${GR}18`)}>Yes</span> : <span style={{color:MU}}>No</span>}</td>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.67rem"}}>{fmt(e.reservationDate)}</span></td>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.67rem"}}>{fmt(e.firstContactDate)}</span></td>
                    <td style={S.td}>
                      {e.deliveryDate
                        ? <span style={S.badge(GR,`${GR}18`)}>{fmt(e.deliveryDate)}</span>
                        : <span style={S.badge(AM,`${AM}18`)}>Waiting</span>}
                    </td>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",color:d!==null?AC:MU}}>{d!==null?`${d}d`:"--"}</span></td>
                    <td style={S.td}><button style={S.sm} onClick={()=>flagEntry(e.id)}>Flag</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Submit component (outside App — fixes the input focus bug)
// ---------------------------------------------------------------------------
const Submit = ({ onSuccess }) => {
  const [form,       setForm]       = useState({...BLANK});
  const [formErr,    setFormErr]    = useState("");
  const [formDone,   setFormDone]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sf = useCallback((k, v) => setForm(f => ({...f, [k]:v})), []);

  const colorOptions = form.launchPackage === "yes" ? COLORS_LAUNCH_PKG : COLORS_STANDARD;

  const submitEntry = async e => {
    e.preventDefault();
    if (!form.reservationDate || !form.state || !form.color || !form.orderType || !form.firstName) {
      setFormErr("Please complete all required fields before submitting."); return;
    }
    setSubmitting(true); setFormErr("");
    try {
      const newEntry = { ...form, submittedAt: new Date().toISOString().slice(0,10), flags:0, deleted:false };
      const result = await airtablePost(toAirtable(newEntry));
      if (result.error) throw new Error(result.error.message);
      setFormDone(true); setForm({...BLANK});
    } catch (err) {
      setFormErr("Submission failed. Please try again in a moment."); console.error(err);
    }
    setSubmitting(false);
  };

  if (formDone) return (
    <div style={{maxWidth:680, margin:"0 auto"}}>
      <div style={{...S.card, textAlign:"center", padding:"2.5rem"}}>
        <div style={{color:GR, fontFamily:"'Bebas Neue',sans-serif", fontSize:"1.8rem", marginBottom:"0.7rem"}}>Submitted. Thank you.</div>
        <p style={{color:MU, fontSize:"0.88rem", marginBottom:"1.3rem", lineHeight:1.7}}>
          Your entry is now live in the tracker. When Rivian contacts you or you take delivery, email
          <strong style={{color:TX}}> contact@nexusevnews.com</strong> with your first name, last initial, and state
          and we'll update your entry.
        </p>
        <div style={{display:"flex", gap:10, justifyContent:"center"}}>
          <button style={S.btn} onClick={onSuccess}>View Dashboard</button>
          <button style={S.ghost} onClick={()=>setFormDone(false)}>Add Another Entry</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{maxWidth:680, margin:"0 auto"}}>
      <h1 style={S.h1}>Add Your <span style={{color:AC}}>R2</span></h1>
      <p style={{color:MU, fontSize:"0.88rem", marginBottom:"1.3rem", lineHeight:1.75}}>
        Every submission makes the tracker more useful for everyone waiting. Fill in everything you know: your name, reservation date, location, and configuration. The more complete your entry, the better the data gets for the whole community.
        <br/><br/>
        You'll only need to contact us twice after submitting. When Rivian reaches out to you about your order, and when you take delivery, email <strong style={{color:TX}}>nexusevnews@gmail.com</strong> with your first name, last initial, and state and we'll update your entry both times.
      </p>
      <div style={S.disc}>
        <strong style={{color:AM}}>Note:</strong> All data on this tracker is self-reported by community members and has not been verified by nexusEVnews or by Rivian. This site is not affiliated with or endorsed by Rivian Automotive, Inc. Your first name and last initial will be shown publicly.
      </div>

      <form onSubmit={submitEntry}>
        {formErr && <div style={S.err}>{formErr}</div>}

        <div style={{...S.card, marginBottom:10}}>
          <span style={S.chip}>About you</span>
          <div style={S.fg}>
            <div style={S.fr}>
              <label style={S.lbl}>First name *</label>
              <input style={S.inp} type="text" placeholder="e.g. Jacob"
                value={form.firstName}
                onChange={e => sf("firstName", e.target.value)}/>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Last initial only * <span style={{color:RE, fontWeight:400}}>(one letter — do not enter your full last name)</span></label>
              <input style={S.inp} type="text" placeholder="e.g. H" maxLength={1}
                value={form.lastInitial}
                onChange={e => sf("lastInitial", e.target.value.replace(/[^a-zA-Z]/g,"").slice(0,1).toUpperCase())}/>
            </div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:10}}>
          <span style={S.chip}>Your order</span>
          <div style={S.fg}>
            <div style={S.fr}>
              <label style={S.lbl}>Do you own an R1T or R1S?</label>
              <div style={{display:"flex", gap:"1.2rem", marginTop:"0.15rem"}}>
                {[["yes","Yes, R1 owner"],["no","No"]].map(([v,l]) => (
                  <label key={v} style={{display:"flex",alignItems:"center",gap:"0.38rem",fontSize:"0.88rem",cursor:"pointer",color:form.isLegacy===v?TX:MU}}>
                    <input type="radio" name="leg" value={v} checked={form.isLegacy===v}
                      onChange={()=>sf("isLegacy",v)} style={{accentColor:AC}}/>{l}
                  </label>
                ))}
              </div>
            </div>
            {form.isLegacy==="yes" && (
              <div style={S.fr}>
                <label style={S.lbl}>R1 original delivery date</label>
                <input style={S.inp} type="date" value={form.r1Date} onChange={e=>sf("r1Date",e.target.value)}/>
              </div>
            )}
            <div style={S.fr}>
              <label style={S.lbl}>Order type *</label>
              <select style={S.sel} value={form.orderType} onChange={e=>sf("orderType",e.target.value)}>
                <option value="">Select</option>
                {ORDERS.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>R2 reservation date *</label>
              <input style={S.inp} type="date" value={form.reservationDate} onChange={e=>sf("reservationDate",e.target.value)}/>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Do you have Launch Package?</label>
              <div style={{display:"flex", gap:"1.2rem", marginTop:"0.15rem"}}>
                {[["yes","Yes"],["no","No / Not sure"]].map(([v,l]) => (
                  <label key={v} style={{display:"flex",alignItems:"center",gap:"0.38rem",fontSize:"0.88rem",cursor:"pointer",color:form.launchPackage===v?TX:MU}}>
                    <input type="radio" name="lp" value={v} checked={form.launchPackage===v}
                      onChange={()=>sf("launchPackage",v)} style={{accentColor:AC}}/>{l}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:10}}>
          <span style={S.chip}>Location</span>
          <div style={S.fg}>
            <div style={S.fr}>
              <label style={S.lbl}>State *</label>
              <select style={S.sel} value={form.state} onChange={e=>sf("state",e.target.value)}>
                <option value="">Select a state</option>
                {STATES.map(st=><option key={st} value={st}>{st}</option>)}
              </select>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Nearest city or metro area</label>
              <input style={S.inp} type="text" placeholder="e.g. Los Angeles, Denver"
                value={form.hub}
                onChange={e => sf("hub", e.target.value)}/>
            </div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:10}}>
          <span style={S.chip}>Configuration — R2 Performance</span>
          <div style={S.fg}>
            <div style={S.fr}>
              <label style={S.lbl}>Exterior color *</label>
              <select style={S.sel} value={form.color} onChange={e=>sf("color",e.target.value)}>
                <option value="">Select</option>
                {colorOptions.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
              {form.launchPackage==="yes" && (
                <div style={{fontSize:"0.7rem",color:MU,marginTop:"0.3rem",fontFamily:"'DM Mono',monospace"}}>
                  Launch Green is only available with Launch Package.
                </div>
              )}
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Interior color</label>
              <select style={S.sel} value={form.interior} onChange={e=>sf("interior",e.target.value)}>
                <option value="">Select</option>
                {INTERIORS.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Wheels</label>
              <select style={S.sel} value={form.wheels} onChange={e=>sf("wheels",e.target.value)}>
                <option value="">Select</option>
                {WHEELS.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:10}}>
          <span style={S.chip}>First Purchase Contact and Delivery Date (optional — update us later)</span>
          <div style={S.fg}>
            <div style={S.fr}>
              <label style={S.lbl}>Date of contact to initiate purchase</label>
              <input style={S.inp} type="date" value={form.firstContactDate} onChange={e=>sf("firstContactDate",e.target.value)}/>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>What kind of contact?</label>
              <select style={S.sel} value={form.contactType} onChange={e=>sf("contactType",e.target.value)}>
                <option value="">Select</option>
                {CONTACTS.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div style={S.fr}>
              <label style={S.lbl}>Actual delivery date</label>
              <input style={S.inp} type="date" value={form.deliveryDate} onChange={e=>sf("deliveryDate",e.target.value)}/>
            </div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:20}}>
          <span style={S.chip}>A little about you (optional)</span>
          <div style={{...S.fg, marginBottom:"0.9rem"}}>
            <div style={S.fr}>
              <label style={S.lbl}>How did you find this tracker?</label>
              <select style={S.sel} value={form.referral} onChange={e=>sf("referral",e.target.value)}>
                <option value="">Select</option>
                {REFERRALS.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div style={S.fr}>
            <label style={S.lbl}>Notes (optional, 400 char max)</label>
            <textarea style={{...S.inp,height:78,resize:"vertical"}} value={form.notes}
              onChange={e=>sf("notes",e.target.value)}
              placeholder="Any relevant context. No personal information." maxLength={400}/>
          </div>
        </div>

        <div style={{display:"flex", gap:10}}>
          <button type="submit" style={{...S.btn, opacity:submitting?0.6:1}} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Entry"}
          </button>
          <button type="button" style={S.ghost} onClick={()=>{setForm({...BLANK});setFormErr("");}}>Clear Form</button>
        </div>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Admin component (outside App)
// ---------------------------------------------------------------------------
const Admin = ({ entries, allEntries, setAllEntries, setEntries, exportCSV, loadAdminData, aLoading }) => {
  const [aAuthed, setAAuthed] = useState(false);
  const [aPass,   setAPass]   = useState("");
  const [aErr,    setAErr]    = useState("");
  const [aView,   setAView]   = useState("all");

  const deleteEntry  = async id => {
    setAllEntries(prev => prev.map(e => e.id===id ? {...e,deleted:true}  : e));
    setEntries(prev => prev.filter(e => e.id!==id));
    await airtablePatch(id, { Deleted: true });
  };
  const restoreEntry = async id => {
    setAllEntries(prev => prev.map(e => e.id===id ? {...e,deleted:false} : e));
    const restored = allEntries.find(e => e.id===id);
    if (restored) setEntries(prev => [{...restored,deleted:false}, ...prev]);
    await airtablePatch(id, { Deleted: false });
  };

  if (!aAuthed) return (
    <div style={{maxWidth:380}}>
      <h1 style={S.h1}>Admin</h1>
      <p style={{color:MU, fontSize:"0.85rem", marginBottom:"1.3rem"}}>Enter the admin password to manage entries and export data.</p>
      <div style={S.fr}>
        <label style={S.lbl}>Password</label>
        <input style={S.inp} type="password" value={aPass} onChange={e=>setAPass(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"){ if(aPass===APASS){setAAuthed(true);setAErr("");loadAdminData();}else setAErr("Wrong password."); }}}/>
      </div>
      {aErr && <div style={{color:RE, fontSize:"0.79rem", marginBottom:"0.75rem"}}>{aErr}</div>}
      <button style={S.btn} onClick={()=>{ if(aPass===APASS){setAAuthed(true);setAErr("");loadAdminData();}else setAErr("Wrong password."); }}>Log In</button>
    </div>
  );

  const flagged  = allEntries.filter(e => (e.flags||0)>=3 && !e.deleted);
  const deleted  = allEntries.filter(e => e.deleted);
  const viewList = aView==="flagged" ? flagged : aView==="deleted" ? deleted : allEntries;

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:"1.4rem"}}>
        <div>
          <h1 style={{...S.h1, marginBottom:4}}>Admin Panel</h1>
          <span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.6rem",color:MU}}>
            {allEntries.filter(e=>!e.deleted).length} active &nbsp;|&nbsp; {flagged.length} flagged &nbsp;|&nbsp; {deleted.length} deleted
          </span>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button style={S.ghost} onClick={exportCSV}>Export CSV</button>
          <button style={S.ghost} onClick={loadAdminData}>Refresh</button>
          <button style={{...S.ghost,color:AM}} onClick={()=>{setAAuthed(false);setAPass("");}}>Log Out</button>
        </div>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:"1.3rem"}}>
        {[["all","All entries"],["flagged",`Flagged (${flagged.length})`],["deleted","Deleted"]].map(([v,l])=>(
          <button key={v} style={aView===v?S.btn:S.ghost} onClick={()=>setAView(v)}>{l}</button>
        ))}
      </div>

      {aLoading ? (
        <div style={{...S.card,textAlign:"center",padding:"2rem",color:MU}}>Loading all records from Airtable...</div>
      ) : viewList.length===0 ? (
        <div style={{...S.card,textAlign:"center",padding:"2rem",color:MU}}>Nothing here.</div>
      ) : (
        <div style={S.tw}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr>{["Submitted","Name","State","Color","Interior","Wheels","Launch Pkg","Reservation","Delivery","Days","Flags","Actions"]
                .map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {viewList.map((e,i) => {
                const d = daysGap(e.firstContactDate, e.deliveryDate);
                const bg = (e.flags||0)>=3 ? `${AM}10` : e.deleted ? `${RE}08` : i%2===0?SU:`${S2}88`;
                const name = [e.firstName, e.lastInitial ? e.lastInitial+"." : ""].filter(Boolean).join(" ") || "--";
                return (
                  <tr key={e.id} style={{background:bg}}>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.67rem",color:MU}}>{fmt(e.submittedAt)}</span></td>
                    <td style={S.td}>{name}</td>
                    <td style={S.td}>{e.state||"--"}</td>
                    <td style={S.td}>{e.color||"--"}</td>
                    <td style={S.td}>{e.interior ? e.interior.split(" (")[0] : "--"}</td>
                    <td style={S.td}>{e.wheels ? e.wheels.split(" (")[0] : "--"}</td>
                    <td style={S.td}>{e.launchPackage==="yes"?"Yes":"No"}</td>
                    <td style={S.td}><span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.67rem"}}>{fmt(e.reservationDate)}</span></td>
                    <td style={S.td}>
                      {e.deliveryDate
                        ? <span style={S.badge(GR,`${GR}18`)}>{fmt(e.deliveryDate)}</span>
                        : <span style={{color:MU}}>--</span>}
                    </td>
                    <td style={S.td}>{d!==null?`${d}d`:"--"}</td>
                    <td style={S.td}><span style={{color:(e.flags||0)>=3?AM:MU,fontFamily:"'DM Mono',monospace"}}>{e.flags||0}</span></td>
                    <td style={S.td}>
                      {e.deleted
                        ? <button style={S.rst} onClick={()=>restoreEntry(e.id)}>Restore</button>
                        : <button style={S.del}  onClick={()=>deleteEntry(e.id)}>Delete</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{marginTop:"1.4rem",padding:"1rem",background:S2,borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:"0.62rem",color:MU,lineHeight:1.9}}>
        To update a submission (contact date, delivery date): edit the row directly in Airtable, then click Refresh above.<br/>
        Admin password is set in the APASS constant at the top of App.js.<br/>
        Deleted entries are soft-deleted and can be restored here.
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------
export default function App() {
  const [tab, setTab] = useState(() => {
    return window.location.hash === "#admin" ? "admin" : "overview";
  });
  const [entries,    setEntries]    = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [aLoading,   setALoading]   = useState(false);
  const [emailVal,   setEmailVal]   = useState("");
  const [emailDone,  setEmailDone]  = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [flagMsg,    setFlagMsg]    = useState("");
  const [estimateOpen, setEstimateOpen] = useState(false);

  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === "#admin") setTab("admin");
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const l = document.createElement("link");
    l.rel  = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&family=DM+Mono:wght@400;500&display=swap";
    document.head.appendChild(l);
    document.body.style.background = "#ffffff";
    document.body.style.margin = "0";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";
    document.documentElement.style.background = "#ffffff";
    return () => { try { document.head.removeChild(l); } catch {} };
  }, []);

  useEffect(() => {
    const CACHE_KEY = "r2tracker_cache";
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    (async () => {
      // Step 1: Show cached data instantly if available and fresh enough
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, ts } = JSON.parse(cached);
          if (Date.now() - ts < CACHE_TTL) {
            setEntries(data);
            setLoading(false);
            // Still fetch fresh in background — don't return
          }
        }
      } catch {}

      // Step 2: Fetch fresh data from Airtable
      try {
        const records = await fetchAll();
        const fresh = records.map(fromAirtable);
        setEntries(fresh);
        // Save to cache
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: fresh, ts: Date.now() }));
        } catch {}
      } catch (e) {
        setError("Could not load tracker data. Check your Airtable connection.");
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  const loadAdminData = async () => {
    setALoading(true);
    try {
      const records = await fetchAllAdmin();
      setAllEntries(records.map(fromAirtable));
    } catch (e) { console.error(e); }
    setALoading(false);
  };

  const flagEntry = async id => {
    const entry = entries.find(e => e.id === id);
    if (!entry) return;
    const newFlags = (entry.flags || 0) + 1;
    setEntries(prev => prev.map(e => e.id===id ? {...e, flags:newFlags} : e));
    await airtablePatch(id, { Flags: newFlags });
    setFlagMsg("Flagged for review."); setTimeout(() => setFlagMsg(""), 2500);
  };

  const exportCSV = () => {
    const src = allEntries.length ? allEntries : entries;
    const hdr = ["ID","Submitted","FirstName","LastInitial","Type","Legacy","State","Hub","Color","Interior","Wheels","LaunchPackage","Reservation","First Contact","Contact Type","Delivery","Days","Referral","Flags","Deleted","Notes"];
    const rows = src.map(e => [
      e.id, e.submittedAt, e.firstName, e.lastInitial,
      e.orderType, e.isLegacy==="yes"?"Yes":"No",
      e.state, e.hub, e.color, e.interior, e.wheels,
      e.launchPackage==="yes"?"Yes":"No",
      e.reservationDate, e.firstContactDate, e.contactType,
      e.deliveryDate, daysGap(e.firstContactDate,e.deliveryDate)??"",
      e.referral, e.flags, e.deleted?"Yes":"No",
      `"${(e.notes||"").replace(/"/g,'""')}"`
    ]);
    const csv = [hdr,...rows].map(r=>r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv,"+encodeURIComponent(csv);
    a.download = "r2_tracker_export.csv";
    a.click();
  };

  const stats = useMemo(() => {
    const dlv  = entries.filter(e => e.deliveryDate);
    const days = dlv.filter(e => e.firstContactDate)
      .map(e => daysGap(e.firstContactDate, e.deliveryDate))
      .filter(d => d !== null && d >= 0);
    return {
      total:     entries.length,
      delivered: dlv.length,
      avgDays:   days.length ? Math.round(days.reduce((a,b)=>a+b,0)/days.length) : null,
      states:    new Set(entries.map(e=>e.state).filter(Boolean)).size,
    };
  }, [entries]);

  const charts = useMemo(() => {
    const dlv = entries.filter(e => e.deliveryDate);
    const wMap = {};
    dlv.forEach(e => { const w=wkOf(e.deliveryDate); if(w) wMap[w]=(wMap[w]||0)+1; });
    const timeline = Object.entries(wMap).sort(([a],[b])=>a>b?1:-1)
      .map(([w,n]) => ({ label:w.slice(5).replace("-","/"), n }));
    const cMap={}, sMap={}, iMap={};
    entries.forEach(e => {
      if (e.color) { const c = e.color.split(" (")[0]; cMap[c] = (cMap[c]||0)+1; }
      if (e.state)    sMap[e.state]    = (sMap[e.state]||0)+1;
      if (e.interior) { const k=e.interior.split(" (")[0]; iMap[k]=(iMap[k]||0)+1; }
    });
    return {
      timeline,
      colors:    Object.entries(cMap).sort(([,a],[,b])=>b-a).map(([n,v])=>({n,v})),
      states:    Object.entries(sMap).sort(([,a],[,b])=>b-a).slice(0,12).map(([n,v])=>({n,v})),
      interiors: Object.entries(iMap).sort(([,a],[,b])=>b-a).map(([n,v])=>({n,v})),
    };
  }, [entries]);

  return (
    <div style={S.app}>
      {estimateOpen && <EstimateModal entries={entries} onClose={()=>setEstimateOpen(false)}/>}
      <header style={{...S.hdr, justifyContent:"space-between"}}>

        {/* LEFT: R2 Tracker in Bebas Neue */}
        <div style={{flex:"0 0 auto", display:"flex", alignItems:"center"}}>
          <span style={{
            fontFamily:"'Bebas Neue',sans-serif",
            fontSize:"1.35rem",
            letterSpacing:"0.06em",
            color:"#e8eaf0",
            lineHeight:1,
            whiteSpace:"nowrap",
          }}>R2 <span style={{color:AC}}>Tracker</span></span>
        </div>

        {/* CENTER: nav */}
        <nav style={{display:"flex", alignItems:"center", gap:"0.3rem", flex:"1 1 auto", justifyContent:"center", padding:"0 0.5rem"}}>
          <button style={S.nb(tab==="overview")} onClick={()=>setTab("overview")}>Overview</button>
          <button
            onClick={()=>setTab("submit")}
            style={{
              background: tab==="submit" ? "#00b8d9" : AC,
              color:"#000",
              border:"none",
              borderRadius:4,
              padding:"0.38rem 0.75rem",
              fontSize:"0.65rem",
              fontWeight:700,
              letterSpacing:"0.04em",
              textTransform:"uppercase",
              cursor:"pointer",
              fontFamily:"'DM Sans',sans-serif",
              boxShadow: tab==="submit" ? "none" : `0 0 12px ${AC}55`,
              whiteSpace:"nowrap",
            }}
          >Submit Yours</button>
          <button style={S.nb(tab==="deliveries")} onClick={()=>setTab("deliveries")}>Deliveries</button>
        </nav>

        {/* RIGHT: nexusEVnews logo */}
        <div style={{flex:"0 0 auto", display:"flex", alignItems:"center", justifyContent:"flex-end"}}>
          <Logo/>
        </div>

      </header>

      <main style={S.main}>
        {tab==="overview" && (
          <Overview
            entries={entries} loading={loading} error={error}
            stats={stats} charts={charts} setTab={setTab}
            emailVal={emailVal} setEmailVal={setEmailVal}
            emailDone={emailDone} setEmailDone={setEmailDone}
            copied={copied} setCopied={setCopied}
            openEstimate={()=>setEstimateOpen(true)}
          />
        )}
        {tab==="deliveries" && (
          <Deliveries
            entries={entries} loading={loading}
            flagEntry={flagEntry} flagMsg={flagMsg}
            exportCSV={exportCSV}
          />
        )}
        {tab==="submit" && (
          <Submit onSuccess={() => setTab("overview")}/>
        )}
        {tab==="admin" && (
          <Admin
            entries={entries} allEntries={allEntries}
            setAllEntries={setAllEntries} setEntries={setEntries}
            exportCSV={exportCSV} loadAdminData={loadAdminData}
            aLoading={aLoading}
          />
        )}
      </main>

      <footer style={S.ftr}>
        <div>
          <a href="https://nexusevnews.com" style={S.lnk}>nexusEVnews</a> &nbsp;·&nbsp;
          <a href="https://nexusevnews.com/about.html" style={S.lnk}>About</a> &nbsp;·&nbsp;
          <a href="https://nexusevnews.com/contact.html" style={S.lnk}>Contact</a> &nbsp;·&nbsp;
          <a href="https://nexusevnews.com/privacy.html" style={S.lnk}>Privacy</a> &nbsp;·&nbsp;
          <a href="https://nexusevnews.com/terms.html" style={S.lnk}>Terms</a>
        </div>
        <div style={{marginTop:"0.35rem"}}>
          All delivery data is self-reported by community members. This tracker is not affiliated with or endorsed by Rivian Automotive, Inc. Rivian is a trademark of Rivian Automotive.
        </div>
        <div>&copy; 2026 nexusEVnews. Independent. No brand deals.</div>
      </footer>
    </div>
  );
}
